import React, { useEffect, ReactNode } from "react";
import { createPortal } from "react-dom";
import Button from "./Button/Button";

interface ModalProps {
  isOpen: boolean;
  title?: string;
  content?: ReactNode;
  onClose: () => void;
  children: React.ReactNode;
  modalBg?: string;
  crossColor?: string;
  textBg?: string;
  hideCloseIcon?: boolean;
  isContentWidth?: boolean;
  alignCrossIcon?: string;
  closeModalOnOutsideClick?: boolean;
  /** Tailwind max-width class for the dialog, e.g. "max-w-[45%]" */
  maxWidthClass?: string;
  /** Clicking the body closes the modal. Defaults to `hideCloseIcon`. */
  closeOnContentClick?: boolean;
}

const Modal: React.FC<ModalProps> = ({
  isOpen,
  title,
  content,
  onClose,
  children,
  textBg = "black",
  modalBg = "bg-white",
  crossColor = "text-black",
  hideCloseIcon,
  isContentWidth,
  alignCrossIcon = "between",
  closeModalOnOutsideClick = true,
  maxWidthClass = "max-w-[90%]",
  closeOnContentClick,
}) => {
  const bodyClosesModal = closeOnContentClick ?? hideCloseIcon;
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onClose();
      }
    };
    if (isOpen) {
      document.addEventListener("keydown", handleEscape);
    } else {
      document.removeEventListener("keydown", handleEscape);
    }
    return () => {
      document.removeEventListener("keydown", handleEscape);
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return createPortal(
    <div
      className="m-auto fixed overflow-y-scroll z-[9999] inset-0 bg-gray-200 bg-opacity-50 flex items-center justify-center pt-2"
      onClick={closeModalOnOutsideClick ? onClose : null}
    >
      <div
        className={`${modalBg} rounded-lg shadow-lg ${
          isContentWidth ? "" : "w-[90%]"
        } ${maxWidthClass} mx-auto px-4 py-3 relative max-h-[90vh] overflow-y-auto scroll`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className={`flex justify-${alignCrossIcon} items-center pb-3`}>
          {title && (
            <h2 className={`text-${textBg} text-xl font-semibold`}>{title}</h2>
          )}
          {!hideCloseIcon && (
            <Button onClick={onClose} className={`text-lg ${crossColor}`}>
              <svg
                className="w-5 h-5"
                fill="black"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M6 18L18 6M6 6l12 12"
                ></path>
              </svg>
            </Button>
          )}
        </div>
        <div
          className="overflow-y-scroll h-fit"
          onClick={bodyClosesModal ? onClose : null}
        >
          {children}
        </div>
      </div>
    </div>,
    document.body
  );
};

export default Modal;
